"use client";

import { TECH_CATEGORIES, techIconUrl, type Tech } from "@/constants";
import { useTechFilter } from "@/hooks/useTechFilter";

export default function TechFilter() {
  const { active, setActive, filtered } = useTechFilter();

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap justify-center gap-2">
        {TECH_CATEGORIES.map((c) => {
          const isActive = c === active;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              aria-pressed={isActive}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                isActive
                  ? "border-transparent bg-[var(--accent)] text-[#07111f] shadow-[0_0_18px_-6px_var(--accent)]"
                  : "border-[var(--stroke)] text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--text)]"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {filtered.map((t) => (
          <TechCard key={`${t.category}-${t.name}`} tech={t} />
        ))}
      </div>
    </div>
  );
}

function TechCard({ tech }: { tech: Tech }) {
  const url = techIconUrl(tech);
  return (
    <article className="group relative flex flex-col items-center gap-3 overflow-hidden rounded-2xl border border-[var(--stroke)] bg-[var(--panel)]/60 p-4 transition duration-300 hover:-translate-y-1 hover:border-[var(--accent)]/50 hover:shadow-[0_20px_50px_-26px_var(--accent)]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100"
        style={{ background: `radial-gradient(circle at 50% 0%, #${tech.color}22, transparent 60%)` }}
      />

      <div
        className="relative grid h-14 w-14 place-items-center rounded-xl ring-1 ring-[var(--stroke)]"
        style={{
          background: url
            ? "rgba(255,255,255,0.04)"
            : `linear-gradient(135deg, #${tech.color}33, #${tech.color}11)`,
        }}
      >
        {url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={url}
            alt={tech.name}
            width={32}
            height={32}
            loading="lazy"
            className="h-8 w-8"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
              const next = e.currentTarget.nextElementSibling as HTMLElement | null;
              if (next) next.style.display = "block";
            }}
          />
        ) : null}
        <span
          className="font-bold"
          style={{
            display: url ? "none" : "block",
            fontSize: 14,
            letterSpacing: 0.5,
            color: `#${tech.color}`,
            textShadow: `0 0 14px #${tech.color}66`,
          }}
          aria-hidden
        >
          {tech.name
            .replace(/[^A-Za-z0-9 ]/g, "")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase())
            .join("")}
        </span>
      </div>

      <p className="relative text-center text-xs font-semibold text-[var(--text)]">
        {tech.name}
      </p>
    </article>
  );
}
